import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { Wallet } from "lucide-react";
import { AdProvider, useAdConfig, SplashScreen } from "@tabipla/components";
import i18n from "./infrastructure/i18n";
import { ApiClientProvider } from "./infrastructure/api";
import { HomePage } from "./presentation/pages/Home";
import { LandingPage } from "./presentation/pages/Landing";
import { GroupsPage } from "./presentation/pages/Groups";
import { GroupDetailPage } from "./presentation/pages/GroupDetail";
import { JoinGroupPage } from "./presentation/pages/JoinGroup";
import { SettingsPage } from "./presentation/pages/Settings";
import { ProfilePage } from "./presentation/pages/Profile";
import { ActivityPage } from "./presentation/pages/Activity";
import { LoginPage } from "./presentation/pages/Login";
import { RegisterPage } from "./presentation/pages/Register";
import { useAuth } from "./application/hooks/useAuth";

// Waritto テーマ
const warittoSplashTheme = {
  backgroundGradient: "from-emerald-50 to-white dark:from-emerald-950 dark:to-gray-900",
  iconBg: "bg-emerald-500",
  iconText: "text-white",
  progressColor: "bg-emerald-500",
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache for persistence
      retry: 1,
    },
  },
});

// Persist query cache to localStorage for offline support
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "waritto-query-cache",
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Shows Landing for unauthenticated users, HomePage for authenticated users
function ConditionalHomeRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <HomePage /> : <LandingPage />;
}

function AppWithAds({ children }: { children: React.ReactNode }) {
  const adConfig = useAdConfig({
    publisherId: import.meta.env.VITE_ADSENSE_PUBLISHER_ID,
    enabled: import.meta.env.VITE_ADS_ENABLED,
    testMode: import.meta.env.VITE_ADS_TEST_MODE,
  });

  return <AdProvider config={adConfig}>{children}</AdProvider>;
}

function AppContent() {
  const { isLoading } = useAuth();

  return (
    <SplashScreen
      appName="Waritto"
      tagline="割り勘を、もっとスマートに"
      icon={<Wallet className="h-8 w-8" />}
      theme={warittoSplashTheme}
      isLoading={isLoading}
    >
      <Routes>
        {/* Home Route - Shows Landing or HomePage based on auth */}
        <Route path="/" element={<ConditionalHomeRoute />} />

        {/* Protected Routes */}
        <Route
          path="/groups"
          element={
            <ProtectedRoute>
              <GroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:id"
          element={
            <ProtectedRoute>
              <GroupDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Open Routes (accessible to everyone) */}
        <Route path="/join/:code" element={<JoinGroupPage />} />
      </Routes>
    </SplashScreen>
  );
}

export function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <ApiClientProvider>
        <AppWithAds>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 1000 * 60 * 60 * 24, // 24 hours
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => {
                  // Only persist successful queries for groups and expenses
                  const queryKey = query.queryKey[0];
                  if (typeof queryKey !== "string") return false;
                  const persistableKeys = ["groups", "group", "sessions", "expenses", "balances"];
                  return (
                    query.state.status === "success" &&
                    persistableKeys.some((key) => queryKey.startsWith(key))
                  );
                },
              },
            }}
          >
            <Toaster
              position="top-center"
              toastOptions={{
                className: "font-sans",
                style: {
                  padding: "12px 16px",
                  borderRadius: "12px",
                },
              }}
              richColors
              closeButton
            />
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </PersistQueryClientProvider>
        </AppWithAds>
      </ApiClientProvider>
    </I18nextProvider>
  );
}
